import { useEffect } from 'react';
import { createSelector } from 'reselect';
import { useRegisterPersistedStore } from '@/stores/PersistenceProvider';
import { type SetStoreState, type StoreInstance, createStore, createStoreInstance } from '@/stores/createStore';
import type { Annotation, AnnotationCondition, AnnotationInterest } from '@/annotation/annotation.types';

const getAnnotationInterestKey = (conditionId: string, capsuleId: string | number) => `${conditionId}_${capsuleId}`;

export interface AnnotationState {
  /** ID of the annotation currently loaded in the journal panel. */
  id: string | undefined;
  /** Sanitized HTML document shown in the journal editor. */
  document: string;
  /** Whether the current journal entry should be treated as a discoverable annotation. */
  isDiscoverable: boolean;
  /** Whether the journal editor is shown in its expanded layout. */
  isExpanded: boolean;
  /** Persisted width of the expanded journal panel. */
  width: number;
  /** Persisted height of the expanded journal panel. */
  height: number;
  /** Whether the journal comments section is expanded. */
  isCommentsExpanded: boolean;
  /** Whether the annotates section is expanded. */
  isAnnotatesExpanded: boolean;
  /** All annotations currently loaded for the active workbook or worksheet context. */
  annotations: Annotation[];
  /** Condition used to store generated annotation capsules for this worksheet. */
  annotationCondition: AnnotationCondition | undefined;
  /** Annotation ID that should be briefly highlighted in the overview. */
  highlightId: string;
  /** Whether the current journal document has been migrated to CKEditor content. */
  isCkEnabled: boolean;
}

export type AnnotationDehydratedState = Omit<
  AnnotationState,
  'annotations' | 'annotationCondition' | 'document' | 'highlightId' | 'isDiscoverable'
>;

export interface AnnotationActions {
  setId: (id?: string) => void;
  setDocument: (document?: string) => void;
  setIsDiscoverable: (isDiscoverable?: boolean) => void;
  setExpanded: (expand: boolean) => void;
  setWidth: (width: number) => void;
  setHeight: (height: number) => void;
  setCommentsExpanded: (expand: boolean) => void;
  setAnnotatesExpanded: (expand: boolean) => void;
  setAnnotations: (annotations: Annotation[]) => void;
  /** Replace an annotation if it already exists, or append it when it is newly loaded. */
  setAnnotation: (id: string | undefined, annotation: Annotation) => void;
  setAnnotationCondition: (annotationCondition: AnnotationCondition | undefined) => void;
  setHighlightId: (id?: string) => void;
  setIsCkEnabled: (isCkEnabled: boolean) => void;
  reset: () => void;
}

export const buildInitialAnnotationState = (): AnnotationState => ({
  id: undefined,
  document: '',
  isDiscoverable: false,
  isExpanded: false,
  width: 0,
  height: 0,
  isCommentsExpanded: false,
  isAnnotatesExpanded: false,
  annotations: [],
  annotationCondition: undefined,
  highlightId: '',
  isCkEnabled: false,
});

export const buildAnnotatedItemIds = (annotations: Annotation[]): Record<string, string> => {
  const map: Record<string, string> = {};

  for (const annotation of annotations) {
    if (annotation.id && annotation.interests) {
      for (const interest of annotation.interests) {
        if (interest.item?.id) {
          map[interest.item.id] = annotation.id;

          if (interest.capsule?.id) {
            map[getAnnotationInterestKey(interest.item.id, interest.capsule.id)] = annotation.id;
          }
        }
      }
    }
  }

  return map;
};

export const findJournalEntries = (annotations: Annotation[], worksheetId: string | undefined) => {
  if (!worksheetId) {
    return [];
  }

  return annotations.filter(
    (annotation) =>
      !annotation.discoverable &&
      annotation.interests?.some((interest: AnnotationInterest) => interest.item?.id === worksheetId),
  );
};

export const findExclusiveEntry = (annotations: Annotation[], workItemId: string | undefined) => {
  if (!workItemId) {
    return [];
  }

  return annotations.filter(
    (annotation) =>
      !annotation.discoverable &&
      annotation.interests?.length === 1 &&
      annotation.interests[0]?.item?.id === workItemId,
  );
};

export const dehydrateAnnotationState = ({
  id,
  isExpanded,
  width,
  height,
  isCommentsExpanded,
  isAnnotatesExpanded,
  isCkEnabled,
}: AnnotationState): AnnotationDehydratedState => ({
  id,
  isExpanded,
  width,
  height,
  isCommentsExpanded,
  isAnnotatesExpanded,
  isCkEnabled,
});

const annotationActionsFactory = (set: SetStoreState<AnnotationState>): AnnotationActions => ({
  setId: (id) =>
    set((draft) => {
      draft.id = id;
    }),

  setDocument: (document = '') =>
    set((draft) => {
      draft.document = document;
    }),

  setIsDiscoverable: (isDiscoverable = false) =>
    set((draft) => {
      draft.isDiscoverable = isDiscoverable;
    }),

  setExpanded: (expand) =>
    set((draft) => {
      draft.isExpanded = expand;
    }),

  setWidth: (width) =>
    set((draft) => {
      draft.width = width;
    }),

  setHeight: (height) =>
    set((draft) => {
      draft.height = height;
    }),

  setCommentsExpanded: (expand) =>
    set((draft) => {
      draft.isCommentsExpanded = expand;
    }),

  setAnnotatesExpanded: (expand) =>
    set((draft) => {
      draft.isAnnotatesExpanded = expand;
    }),

  setAnnotations: (annotations) =>
    set((draft) => {
      draft.annotations = annotations;
    }),

  setAnnotation: (id, annotation) =>
    set((draft) => {
      if (!id) {
        draft.annotations.push(annotation);
        return;
      }

      const index = draft.annotations.findIndex((existingAnnotation) => existingAnnotation.id === id);
      if (index >= 0) {
        draft.annotations[index] = annotation;
      } else {
        draft.annotations.push(annotation);
      }
    }),

  setAnnotationCondition: (annotationCondition) =>
    set((draft) => {
      draft.annotationCondition = annotationCondition;
    }),

  setHighlightId: (id = '') =>
    set((draft) => {
      draft.highlightId = id;
    }),

  setIsCkEnabled: (isCkEnabled) =>
    set((draft) => {
      draft.isCkEnabled = isCkEnabled;
    }),

  reset: () =>
    set((draft) => {
      Object.assign(draft, buildInitialAnnotationState());
    }),
});

const [AnnotationStoreProvider, useAnnotationStoreInstance, useAnnotationSelector] = createStore(
  'Annotation',
  buildInitialAnnotationState(),
  annotationActionsFactory,
);

const selectAnnotations = (state: AnnotationState) => state.annotations;
export const selectAnnotatedItemIds = createSelector(selectAnnotations, buildAnnotatedItemIds);
const journalEntriesSelector = createSelector(
  selectAnnotations,
  (_state: AnnotationState, worksheetId: string | undefined) => worksheetId,
  findJournalEntries,
);
const exclusiveEntriesSelector = createSelector(
  selectAnnotations,
  (_state: AnnotationState, workItemId: string | undefined) => workItemId,
  findExclusiveEntry,
);

export type LegacyAnnotationStore = StoreInstance<AnnotationState, AnnotationActions> & {
  readonly persistenceLevel: 'WORKBOOK';
  dehydrate: () => AnnotationDehydratedState;
  rehydrate: (dehydratedState: Partial<AnnotationDehydratedState>) => void;
  findJournalEntries: typeof findJournalEntries;
  findExclusiveEntry: typeof findExclusiveEntry;
};

export function legacyCreateAnnotationStore(): LegacyAnnotationStore {
  const store = createStoreInstance(buildInitialAnnotationState(), annotationActionsFactory);

  return Object.assign(store, {
    persistenceLevel: 'WORKBOOK' as const,
    dehydrate: () => dehydrateAnnotationState(store.getState()),
    rehydrate: (dehydratedState: Partial<AnnotationDehydratedState>) => {
      store.setState(dehydratedState, false);
    },
    findJournalEntries,
    findExclusiveEntry,
  }) as LegacyAnnotationStore;
}

export { AnnotationStoreProvider };

export const useAnnotationId = () => useAnnotationSelector((state) => state.id);
export const useAnnotationDocument = () => useAnnotationSelector((state) => state.document);
export const useAnnotationIsDiscoverable = () => useAnnotationSelector((state) => state.isDiscoverable);
export const useAnnotationIsExpanded = () => useAnnotationSelector((state) => state.isExpanded);
export const useAnnotationWidth = () => useAnnotationSelector((state) => state.width);
export const useAnnotationHeight = () => useAnnotationSelector((state) => state.height);
export const useAnnotationIsCommentsExpanded = () => useAnnotationSelector((state) => state.isCommentsExpanded);
export const useAnnotationIsAnnotatesExpanded = () => useAnnotationSelector((state) => state.isAnnotatesExpanded);
export const useAnnotations = () => useAnnotationSelector(selectAnnotations);
export const useAnnotationCondition = () => useAnnotationSelector((state) => state.annotationCondition);
export const useAnnotationHighlightId = () => useAnnotationSelector((state) => state.highlightId);
export const useAnnotationIsCkEnabled = () => useAnnotationSelector((state) => state.isCkEnabled);
export const useAnnotatedItemIds = () => useAnnotationSelector(selectAnnotatedItemIds);

/** Returns the journal entries associated with the supplied worksheet or workbook item. */
export const useJournalEntries = (worksheetId: string | undefined) =>
  useAnnotationSelector((state) => journalEntriesSelector(state, worksheetId));

/** Returns annotations that are exclusively scoped to the supplied worksheet or workbook item. */
export const useExclusiveEntries = (workItemId: string | undefined) =>
  useAnnotationSelector((state) => exclusiveEntriesSelector(state, workItemId));

export const useAnnotationStoreActions = () => useAnnotationStoreInstance().actions;

export function createTestAnnotationStore(initialState: Partial<AnnotationState> = {}) {
  const store = createStoreInstance(buildInitialAnnotationState(), annotationActionsFactory);
  store.setState(initialState, false);
  return store;
}

export const usePersistAnnotationStore = () => {
  const register = useRegisterPersistedStore();
  const store = useAnnotationStoreInstance();

  useEffect(() => {
    return register({
      storeName: 'sqAnnotationStore',
      persistenceLevel: 'WORKBOOK',
      store,
      dehydrate: dehydrateAnnotationState,
      reset: (initialState) => {
        store.setState(initialState, true);
      },
    });
  }, [register, store]);
};
